<nb-card class="form">
  <nb-card-header>
    <h4>{{ "dialogs.moodleImport.title" | translate }}</h4>
  </nb-card-header>

  <nb-card-body>
    <!-- Loading state -->
    <div *ngIf="loading" class="loading-state">
      <nb-spinner status="primary" size="medium"></nb-spinner>
      <p>{{ "dialogs.moodleImport.loadingCourses" | translate }}</p>
    </div>

    <div *ngIf="!loading && !syncing" class="course-search">
      <nb-form-field>
        <nb-icon nbPrefix icon="search-outline"></nb-icon>
        <input
          nbInput
          fullWidth
          fieldSize="small"
          [(ngModel)]="searchInput"
          (keyup.enter)="applyCourseSearch()"
          [placeholder]="'dialogs.moodleImport.searchPlaceholder' | translate"
        />
        <nb-icon
          *ngIf="searchInput || search"
          nbSuffix
          icon="close-outline"
          (click)="clearCourseSearch()"
        ></nb-icon>
      </nb-form-field>
    </div>

    <!-- No courses -->
    <div *ngIf="!loading && courses.length === 0" class="empty-state">
      <nb-icon icon="alert-circle-outline" status="warning"></nb-icon>
      <p>{{ (search ? "dialogs.moodleImport.noSearchResults" : "dialogs.moodleImport.noCourses") | translate }}</p>
    </div>

    <!-- Course list with multi-select -->
    <div *ngIf="!loading && courses.length > 0 && !syncing" class="course-browser">
      <!-- Select All row -->
      <div class="select-all-row" (click)="toggleAll()">
        <nb-checkbox
          [checked]="allSelected"
          [disabled]="selectableCourses.length === 0"
          (checkedChange)="toggleAll()"
          (click)="$event.stopPropagation()"
        ></nb-checkbox>
        <span class="select-all-label">
          {{ "dialogs.moodleImport.selectAll" | translate }}
        </span>
        <span *ngIf="selectedCourses.size > 0" class="counter-chip">
          {{ selectedCourses.size }}
        </span>
      </div>

      <div class="course-list-scroll">
        <nb-list>
          <nb-list-item
            *ngFor="let course of courses"
            (click)="toggleCourse(course)"
            [ngClass]="{ selected: isCourseSelected(course), disabled: isCourseDisabled(course) }"
            [nbTooltip]="isCourseDisabled(course) ? getCourseDisabledHint(course) : ''"
            [nbTooltipDisabled]="!isCourseDisabled(course)"
            nbTooltipPlacement="top"
            class="course-item"
          >
            <div class="course-info">
              <nb-checkbox
                [checked]="isCourseSelected(course)"
                [disabled]="isCourseDisabled(course)"
                (checkedChange)="toggleCourse(course)"
                (click)="$event.stopPropagation()"
              ></nb-checkbox>
              <nb-icon icon="book-outline"></nb-icon>
              <div class="course-labels">
                <p class="course-name">{{ course.fullname }}</p>
                <p class="course-shortname">{{ course.shortname }}</p>
                <p *ngIf="course.site_name || course.site_url" class="course-site">
                  {{ course.site_name || course.site_url }}
                </p>
                <p *ngIf="isCourseDisabled(course)" class="course-disabled-hint">
                  {{ getCourseDisabledHint(course) }}
                </p>
              </div>
            </div>
          </nb-list-item>
        </nb-list>
      </div>

      <div class="course-pagination-bar" *ngIf="courseTotal > coursePageSize">
        <app-pagination
          [totalItems]="courseTotal"
          [itemsPerPage]="coursePageSize"
          [currentPage]="coursePage"
          [pageSizeOptions]="pageSizeOptions"
          (pageChanged)="onCoursePageChange($event)"
          (itemsPerPageChange)="onCoursePageSizeChange($event)"
        ></app-pagination>
      </div>
    </div>

    <!-- Single sync progress -->
    <div *ngIf="syncing && syncJob && !isBulkSync" class="sync-progress">
      <h6>{{ "dialogs.moodleImport.syncing" | translate }}</h6>
      <nb-progress-bar
        [value]="progressPercent"
        status="primary"
        [displayValue]="true"
      ></nb-progress-bar>
      <p class="sync-detail">
        {{ syncJob.processed_items }} / {{ syncJob.total_items }}
        {{ "dialogs.moodleImport.itemsProcessed" | translate }}
      </p>
      <p *ngIf="syncJob.status === 'PENDING'" class="sync-status">
        {{ "dialogs.moodleImport.statusPending" | translate }}
      </p>
    </div>

    <!-- Bulk sync progress -->
    <div *ngIf="syncing && bulkSyncJob" class="sync-progress">
      <h6>{{ "dialogs.moodleImport.syncingBulk" | translate }}</h6>
      <nb-progress-bar
        [value]="progressPercent"
        status="primary"
        [displayValue]="true"
      ></nb-progress-bar>
      <p class="sync-detail" *ngIf="currentSyncLabel">
        {{ currentSyncLabel }}
      </p>
      <p class="sync-detail">
        {{ bulkSyncJob.completed_courses + bulkSyncJob.failed_courses }} / {{ bulkSyncJob.total_courses }}
        {{ "dialogs.moodleImport.coursesProcessed" | translate }}
      </p>
    </div>
  </nb-card-body>

  <nb-card-footer>
    <div class="footer-actions">
      <button
        type="button"
        nbButton
        status="danger"
        outline
        (click)="close()"
      >
        {{ "buttons.close" | translate }}
      </button>

      <div class="footer-right">
        <nb-select
          *ngIf="!syncing && !loading && courses.length > 0"
          [(selected)]="syncFrequency"
          size="small"
          class="freq-select"
          nbTooltip="{{ 'dialogs.moodleImport.syncFrequency' | translate }}"
          nbTooltipPlacement="top"
        >
          <nb-option value="NEVER">{{ "dialogs.moodleImport.freqNever" | translate }}</nb-option>
          <nb-option value="DAILY">{{ "dialogs.moodleImport.freqDaily" | translate }}</nb-option>
          <nb-option value="MONTHLY">{{ "dialogs.moodleImport.freqMonthly" | translate }}</nb-option>
          <nb-option value="YEARLY">{{ "dialogs.moodleImport.freqYearly" | translate }}</nb-option>
        </nb-select>
        <button
          nbButton
          status="primary"
          [disabled]="selectedCourses.size === 0 || syncing || loading"
          [nbSpinner]="syncing"
          (click)="startSync()"
        >
          {{ "dialogs.moodleImport.startSync" | translate }}
          <span *ngIf="selectedCourses.size > 0"> ({{ selectedCourses.size }})</span>
        </button>
      </div>
    </div>
  </nb-card-footer>
</nb-card>
